<template>
  <div class="contract-doc-selector">
    <div
      ref="activator"
      class="contract-doc-selector__activator"
      :class="{ 'contract-doc-selector__activator--disabled': isDisabled }"
    >
      <AppIcon
        :key="statusIconName"
        :name="statusIconName"
        class="contract-doc-selector__status-icon"
        :style="{ color: statusIconColor }"
      />
      <div class="contract-doc-selector__text">
        <span class="contract-doc-selector__title">{{ selectedContract?.contract_name }}</span>
        <span class="contract-doc-selector__subtitle">{{ selectedContract?.outsourcer }}</span>
      </div>
      <AppIcon
        v-if="!isDisabled"
        src="doc-chevron-down.svg"
        class="contract-doc-selector__arrow"
        :class="{ 'contract-doc-selector__arrow--rotated': isOpen }"
      />
    </div>

    <BaseTooltip
      v-if="!isDisabled"
      for="activator"
      trigger="click"
      placement="bottom-start"
      :arrow="false"
      :interactive="true"
      :offset="4"
      append-to="body"
      hide-on-click
      class="contract-doc-selector__dropdown"
      @open="onDropdownOpen"
      @close="isOpen = false"
    >
      <div
        v-for="item in contracts"
        :key="item.contract_id"
        hide-on-select
        class="contract-doc-selector__item"
        @click="selectContract(item.contract_id)"
      >
        <AppIcon
          :key="getStatusIconName(itemStatus(item))"
          :name="getStatusIconName(itemStatus(item))"
          class="contract-doc-selector__item-status"
          :style="{ color: getStatusIconColor(itemStatus(item)) }"
        />
        <div class="contract-doc-selector__item-text">
          <span class="contract-doc-selector__item-title">{{ item.contract_name }}</span>
          <span v-if="item.outsourcer" class="contract-doc-selector__item-subtitle">{{ item.outsourcer }}</span>
        </div>
        <AppIcon
          v-if="item.contract_id === selectedId"
          src="doc-check-selected.svg"
          class="contract-doc-selector__item-check"
        />
      </div>
    </BaseTooltip>
  </div>
</template>

<script>
import AppIcon from "@/components/common/Icon.vue";
import BaseTooltip from "@/components/common/BaseTooltip.vue";
import { STATUSES_CONTRACT } from "@/utils/constants";

export default {
  name: "ContractDocumentSelector",

  components: {
    AppIcon,
    BaseTooltip,
  },

  props: {
    contracts: {
      type: Array,
      default: () => [],
    },
    selectedId: {
      type: String,
      default: null,
    },
    contractStatus: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      isOpen: false,
    };
  },

  computed: {
    isDisabled() {
      return !this.contracts.length;
    },

    selectedContract() {
      return this.contracts.find((item) => item.contract_id === this.selectedId) || null;
    },

    statusIconName() {
      return this.getStatusIconName(this.contractStatus);
    },

    statusIconColor() {
      return this.getStatusIconColor(this.contractStatus);
    },
  },

  methods: {
    itemStatus(item) {
      return item.contract_id === this.selectedId ? this.contractStatus : item.contract_status;
    },

    getStatusIconName(status) {
      if (status === "suspended") return "doc-status-suspended";
      if (status === "terminated") return "doc-status-closed";

      return ["service", "concluding_await", "service_datenotarrive"].includes(status)
        ? "contract-status-check"
        : "contract-status-clock";
    },

    getStatusIconColor(status) {
      const key = status === "service_datenotarrive" ? "concluding_await" : status;
      return STATUSES_CONTRACT[key]?.color || STATUSES_CONTRACT.concluding.color;
    },

    selectContract(contractId) {
      this.$emit("select", contractId);
    },

    onDropdownOpen(instance) {
      this.isOpen = true;
      const width = instance.target.getBoundingClientRect().width;
      instance.tooltipElement.style.width = `${width}px`;
    },
  },
};
</script>

<style scoped lang="scss">
/* stylelint-disable selector-class-pattern */
.contract-doc-selector {
  &__activator {
    display: flex;
    padding: 0 12px;
    cursor: pointer;

    &--disabled {
      cursor: default;
      pointer-events: none;
    }
  }

  &__status-icon {
    width: 16px;
    height: 16px;
    flex: none;
    margin-right: 8px;
    margin-top: 1px;
  }

  &__text {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }

  &__title {
    overflow: hidden;
    color: #1c1c1c;
    text-overflow: ellipsis;
    font-family: Roboto, sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 120%;
    white-space: nowrap;
    margin-bottom: 4px;
  }

  &__subtitle {
    overflow: hidden;
    color: var(--Colors-Grey-dark-grey, #6b6b6b);
    text-overflow: ellipsis;
    font-family: Roboto, sans-serif;
    font-size: 13px;
    font-weight: 400;
    line-height: 120%;
    white-space: nowrap;
  }

  &__arrow {
    width: 11px;
    height: 6px;
    flex: none;
    transition: transform 0.3s;
    margin-top: auto;
    margin-bottom: auto;

    &--rotated {
      transform: rotate(180deg);
    }
  }

  &__dropdown {
    padding: 4px 0;
    border-radius: 8px;
    background-color: #fff;
    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.15);
  }

  &__item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 12px;
    cursor: pointer;
    transition: background-color 0.3s;

    &:hover {
      background-color: #f6f6f6;
    }
  }

  &__item-status {
    width: 16px;
    height: 16px;
    flex: none;
  }

  &__item-text {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }

  &__item-title {
    overflow: hidden;
    color: #1c1c1c;
    text-overflow: ellipsis;
    font-family: Roboto, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 120%;
    white-space: nowrap;
  }

  &__item-subtitle {
    overflow: hidden;
    color: var(--Colors-Grey-dark-grey, #6b6b6b);
    text-overflow: ellipsis;
    font-family: Roboto, sans-serif;
    font-size: 13px;
    font-weight: 400;
    line-height: 120%;
    white-space: nowrap;
  }

  &__item-check {
    width: 11px;
    height: 8px;
    flex: none;
    margin-left: auto;
  }
}
</style>
